import { useCallback, useEffect } from "react";

import { Flex } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { routes } from "config/routes";
import { CorrectionsTextSearch } from "flows/Inventory/shared/components/CorrectionsTextSearch";
import { ScanPlaceholder } from "flows/Inventory/shared/components/ScanPlaceholder";
import { InventoryProduct } from "flows/Inventory/shared/models/inventoryProduct/types";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { useBarcodeScanner } from "shared/hooks/useBarcodeScanner";
import { useScan } from "shared/hooks/useScan/useScan";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { isShelfNumberFormat } from "utils/scan";

import { ProductsList } from "../components/ProductsList";
import { useStockCorrectionActions } from "../stores/useStockCorrectionActions";
import { useStockCorrectionStore } from "../stores/useStockCorrectionStore";

export function IdlePage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { processBarcode } = useBarcodeScanner();

  const phase = useStockCorrectionStore((s) => s.phase);
  const searchResults = useStockCorrectionStore((s) => s.inventoryProductsList);

  const isIdle = phase === "idle";
  useEffect(() => {
    if (!isIdle) {
      useStockCorrectionStore.getState().reset();
    }

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isResolvingProducts = phase === "resolvingProducts";
  const isSearching = phase === "search" || phase === "searchLoading";
  const isSearchLoading = phase === "searchLoading";

  const {
    scan,
    activateSearch,
    cancelSearch,
    searchProducts,
    resetSearchResults,
    selectProductAndDeclare,
  } = useStockCorrectionActions();

  const onClickProduct = useCallback(
    (product: InventoryProduct) => {
      selectProductAndDeclare(product.productSku, product.shelf);
    },
    [selectProductAndDeclare],
  );

  const onSearch = useCallback(
    (query: string) => {
      if (query.length >= 3) {
        searchProducts(query);
      } else {
        resetSearchResults();
      }
    },
    [searchProducts, resetSearchResults],
  );

  const onCancel = useCallback(() => {
    cancelSearch();
  }, [cancelSearch]);

  const onFocusSearchBar = useCallback(() => {
    if (!isSearching) {
      activateSearch();
    }
  }, [isSearching, activateSearch]);

  useScan({
    onScan: (scannedValue) => {
      if (!isIdle) return;

      const scanType = isShelfNumberFormat(scannedValue) ? "shelf" : "product";
      let barcode: string | null = scannedValue;

      if (scanType === "product") {
        barcode = processBarcode(scannedValue);
        if (!barcode) return;
      }

      useStockCorrectionStore.getState().setScanType(scanType);
      useStockCorrectionStore.getState().setScannedValue(barcode);
      scan(barcode);
    },
  });

  const searchResultsList = (
    <ProductsList
      variantSize="s"
      products={searchResults}
      onClickProduct={onClickProduct}
      showStockLabel
    />
  );

  return (
    <Page isFull isBgGrey h="100%" justifyContent="start">
      {!isSearching && (
        <NavigationHeader
          title={intl.formatMessage({ id: "components.inventory.idle.header-title" })}
          onClickGoBack={() => navigate(routes.inventory.root)}
        />
      )}

      <CorrectionsTextSearch
        isLoading={isSearchLoading}
        isSearchActive={isSearching}
        searchResults={searchResults}
        onSearch={onSearch}
        onFocusSearchBar={onFocusSearchBar}
        onCancel={onCancel}
        searchResultsList={searchResultsList}
      />

      {!isSearching && (
        <Flex
          flex={1}
          flexDirection="column"
          w="100%"
          overflowY="scroll"
          alignItems="center"
          justifyContent="center"
          p="s200"
        >
          <ScanPlaceholder
            title="pages.inventory.stock-correction.instructions.title"
            subtitle="pages.inventory.stock-correction.instructions.subtitle"
          />
        </Flex>
      )}
      <SpinnerModal isOpen={isResolvingProducts} />
    </Page>
  );
}
